// eslint-disable-next-line no-use-before-define
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { ActionPopoverContextProvider, TenantContext, UserContext } from 'op-contexts';
import { EMRFeature, FeaturesList, UserFeature } from 'op-interfaces/User';
import { TenantConfigQueryData, UserLanguageQueryData } from 'op-interfaces/graph';
import { GET_USER_PROFILE } from 'op-graphql/queries';
import { USER_FEATURE_QUERY } from 'op-components/RO/Base/queries';
import { TENANT_CONFIG_QUERY } from 'op-components/RO/Base/TenantConfigQuery';
import PatientFloatingActionsHost from 'op-components/RO/PatientFloatingActions/PatientFloatingActionsHost';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import dayjs from 'op-utils/dayjs';

const AppShell = ({ children }: PropsWithChildren): JSX.Element => {
  const [hasStaffId, setHasStaffId] = useState(false);
  const [userState, setUserState] = useState('');
  const [navShow, setNavShow] = useState(true);
  const [panelShow, setPanelShow] = useState(true);
  const [primaryRole, setPrimaryRole] = useState('');
  const [isPractitioner, setIsPractitioner] = useState(false);
  const [features, setFeatures] = useState<FeaturesList | null>(null);
  const [timezone, setTimezone] = useState(CurrentAppConfig?.DefaultTimezone);
  const [doseUnit, setDoseUnit] = useState('Gy');
  const [ariaDoseUnit, setAriaDoseUnit] = useState('Gy');
  const [mosaiqDoseUnit, setMosaiqDoseUnit] = useState('cGy');
  const [fractionUnit, setFractionUnit] = useState('#');
  const [name, setName] = useState('');
  const [voidCareplanExpiryPeriod, setVoidCareplanExpiryPeriod] = useState(168);
  const [showTreatmentReview, setShowTreatmentReview] = useState(false);
  const [showManageProtocols, setShowManageProtocols] = useState(false);
  const [showIsocenterField, setShowIsocenterField] = useState(false);

  const { data: userData, loading: userLoading } = useQuery<UserLanguageQueryData>(GET_USER_PROFILE);
  const { data: tenantConfigData } = useQuery<TenantConfigQueryData>(TENANT_CONFIG_QUERY);
  const { data: featuresData, loading: featuresLoading } = useQuery(USER_FEATURE_QUERY);

  const isFeatureLoading = useCallback(() => {
    return !features || featuresLoading;
  }, [features, featuresLoading]);

  const getEMRFeature = useCallback(
    (featureName: string): EMRFeature | undefined => {
      if (features && features?.featureList) {
        return features.featureList?.find((item: any) => item?.feature?.name === featureName);
      }
      return undefined;
    },
    [features],
  );

  const getUserFeature = useCallback(
    (featureName: string): UserFeature | undefined => {
      if (features && features.userFeatureList) {
        return features.userFeatureList.find((item: any) => item?.feature?.name === featureName);
      }
      return undefined;
    },
    [features],
  );

  const hasFeature = useCallback(
    (featureName: string): boolean | undefined => {
      if (!features) {
        return undefined;
      }
      return Boolean(getEMRFeature(featureName)) || Boolean(getUserFeature(featureName));
    },
    [getEMRFeature, getUserFeature, features],
  );

  useEffect(() => {
    if (userData && userData.user) {
      setPrimaryRole(userData.user.primaryRole);
      setHasStaffId(userData.user.hasStaffId);
      setTimezone(userData.user.timezone);
      setIsPractitioner(userData.user.isPractitioner);
      setUserState(userData.user.state || '');
      setName(userData.user.name || '');
    }
    if (userData && userData.language) {
      dayjs.locale(userData.language.language);
    }
  }, [userData, userLoading]);

  useEffect(() => {
    if (tenantConfigData?.tenantConfig) {
      setDoseUnit(tenantConfigData?.tenantConfig?.doseUnit);
      setAriaDoseUnit(tenantConfigData?.tenantConfig?.ariaDoseUnit);
      setMosaiqDoseUnit(tenantConfigData?.tenantConfig?.mosaiqDoseUnit);
      setFractionUnit(tenantConfigData?.tenantConfig?.fractionUnit);
      setVoidCareplanExpiryPeriod(tenantConfigData?.tenantConfig?.voidCareplanExpiryPeriod);
      setShowTreatmentReview(tenantConfigData?.tenantConfig?.showTreatmentReview);
      setShowManageProtocols(tenantConfigData?.tenantConfig?.showManageProtocols);
      setShowIsocenterField(tenantConfigData?.tenantConfig?.showIsocenterField);
    }
  }, [tenantConfigData?.tenantConfig]);

  useEffect(() => {
    if (featuresData) {
      setFeatures(featuresData);
    }
  }, [featuresData]);

  const permissions = userData?.user?.permissions || [];
  if (userLoading || !userData) return <></>;
  return (
    <TenantContext.Provider
      value={{
        doseUnit,
        setDoseUnit,
        ariaDoseUnit,
        setAriaDoseUnit,
        mosaiqDoseUnit,
        setMosaiqDoseUnit,
        fractionUnit,
        setFractionUnit,
        voidCareplanExpiryPeriod,
        setVoidCareplanExpiryPeriod,
        showTreatmentReview,
        setShowTreatmentReview,
        showManageProtocols,
        setShowManageProtocols,
        showIsocenterField,
        setShowIsocenterField,
      }}>
      <UserContext.Provider
        value={{
          state: {
            hasStaffId,
            navShow,
            panelShow,
            primaryRole,
            timezone,
            isPractitioner,
            features,
            userState,
            name,
            permissions,
          },
          setHasStaffId,
          setNavShow,
          setPanelShow,
          setPrimaryRole,
          setTimezone,
          setFeatures,
          setIsPractitioner,
          isFeatureLoading,
          getEMRFeature,
          getUserFeature,
          hasFeature,
          setName,
          hasPermission: (permissionName: string) => permissions.includes(permissionName),
        }}>
        <ActionPopoverContextProvider>
          {children}
          <PatientFloatingActionsHost />
        </ActionPopoverContextProvider>
      </UserContext.Provider>
    </TenantContext.Provider>
  );
};

export default AppShell;
